<template>
  <section class="image-editor" aria-label="图片编辑器">
    <main class="editor-main">
      <header class="editor-heading">
        <div><strong>裁剪与构图</strong><span>拖动图片或裁剪框，精确选择需要保留的区域</span></div>
        <el-button size="mini" icon="el-icon-refresh-left" @click="resetEditor">恢复原图</el-button>
      </header>
      <div class="cropper-stage" @mousedown="snapshotSoon" @touchstart="snapshotSoon"><img ref="image" :src="sourceUrl" :alt="file.name"></div>
      <div class="compact-toolbar" aria-label="图片变换工具">
        <el-button-group>
          <el-tooltip content="撤销" placement="top"><el-button icon="el-icon-back" :disabled="historyIndex <= 0" @click="undo" /></el-tooltip>
          <el-tooltip content="重做" placement="top"><el-button icon="el-icon-right" :disabled="historyIndex >= history.length - 1" @click="redo" /></el-tooltip>
          <el-tooltip content="左转 90°" placement="top"><el-button icon="el-icon-refresh-left" @click="rotate(-90)" /></el-tooltip>
          <el-tooltip content="右转 90°" placement="top"><el-button icon="el-icon-refresh-right" @click="rotate(90)" /></el-tooltip>
          <el-tooltip content="水平翻转" placement="top"><el-button icon="el-icon-sort" @click="flipHorizontal" /></el-tooltip>
          <el-tooltip content="垂直翻转" placement="top"><el-button class="vertical-flip-button" icon="el-icon-sort" @click="flipVertical" /></el-tooltip>
        </el-button-group>
        <div class="zoom-control">
          <i class="el-icon-zoom-out"></i>
          <el-slider v-model="zoomPercent" :min="25" :max="300" :show-tooltip="false" @input="changeZoom" />
          <i class="el-icon-zoom-in"></i>
          <el-input-number v-model="zoomPercent" controls-position="right" :min="25" :max="300" size="mini" @change="changeZoom" />
          <span>%</span>
        </div>
      </div>
    </main>

    <aside class="editor-panel">
      <section class="panel-section preview-section">
        <div class="section-title">实时预览 <span>{{ outputWidth }} × {{ outputHeight }} px</span></div>
        <div class="preview-shell"><div class="cropper-live-preview"></div></div>
      </section>
      <section class="panel-section">
        <div class="section-title">裁剪比例</div>
        <el-radio-group v-model="aspectRatioKey" size="mini" class="ratio-grid" @change="changeAspectRatio">
          <el-radio-button v-for="item in aspectRatios" :key="item.key" :label="item.key">{{ item.label }}</el-radio-button>
        </el-radio-group>
      </section>
      <section class="panel-section">
        <div class="section-title">输出尺寸</div>
        <div class="dimension-row">
          <el-input-number v-model="outputWidth" :min="1" :max="8192" controls-position="right" @change="changeOutputSize('width')" />
          <el-tooltip :content="aspectLocked ? '已锁定宽高比' : '未锁定宽高比'" placement="top">
            <el-button class="ratio-lock" :type="aspectLocked ? 'primary' : 'default'" icon="el-icon-link" @click="toggleAspectLock" />
          </el-tooltip>
          <el-input-number v-model="outputHeight" :min="1" :max="8192" controls-position="right" @change="changeOutputSize('height')" />
        </div>
        <div class="dimension-labels"><span>宽度 px</span><span>高度 px</span></div>
        <div v-if="isUpscaling" class="setting-warning"><i class="el-icon-warning-outline"></i> 输出会放大，清晰度可能下降</div>
      </section>
      <section class="panel-section output-section">
        <div class="section-title">导出设置</div>
        <el-select v-model="outputFormat" size="small" @change="markEdited">
          <el-option v-for="item in formatOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <template v-if="outputMime !== 'image/png'">
          <div class="quality-heading"><label>画质</label><span>{{ quality }}%</span></div>
          <el-slider v-model="quality" :min="50" :max="100" @input="markEdited" />
        </template>
      </section>
      <div class="original-meta"><i class="el-icon-picture-outline"></i><div><strong>{{ file.name }}</strong><span>原图 {{ originalSize.width }} × {{ originalSize.height }} · {{ formatBytes(file.size) }}</span></div></div>
    </aside>
  </section>
</template>

<script>
import Cropper from 'cropperjs'
import 'cropperjs/dist/cropper.css'

const MIME_EXTENSIONS = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' }

export default {
  name: 'ImageUploadEditor',
  props: { file: { type: File, required: true } },
  data() {
    return {
      cropper: null, sourceUrl: '', ready: false, edited: false,
      initialZoom: 1, zoomPercent: 100, scaleX: 1, scaleY: 1,
      aspectRatioKey: 'free', aspectLocked: false, customOutputSize: false,
      outputWidth: 1, outputHeight: 1, quality: 92, outputFormat: 'original',
      originalSize: { width: 0, height: 0 }, cropSize: { width: 1, height: 1 },
      history: [], historyIndex: -1, historyTimer: null, restoringHistory: false,
      aspectRatios: [
        { key: 'free', label: '自由' }, { key: '1:1', label: '1:1' }, { key: '4:3', label: '4:3' },
        { key: '3:2', label: '3:2' }, { key: '16:9', label: '16:9' }, { key: '9:16', label: '9:16' }
      ],
      formatOptions: [
        { value: 'original', label: '保持原格式' }, { value: 'image/jpeg', label: 'JPEG（照片）' },
        { value: 'image/png', label: 'PNG（透明）' }, { value: 'image/webp', label: 'WebP（推荐）' }
      ]
    }
  },
  computed: {
    outputMime() { return this.outputFormat === 'original' ? this.file.type : this.outputFormat },
    isUpscaling() { return this.outputWidth > this.cropSize.width || this.outputHeight > this.cropSize.height }
  },
  watch: { file: { immediate: true, handler() { this.openFile() } } },
  beforeDestroy() { this.destroyCropper(); clearTimeout(this.historyTimer) },
  methods: {
    /** 2026-09-10：本地文件只通过临时 URL 进入编辑器，切图和关闭时立即释放。 */
    openFile() {
      this.destroyCropper(); this.resetState(); this.sourceUrl = URL.createObjectURL(this.file); this.$nextTick(this.createCropper)
    },
    createCropper() {
      if (!this.$refs.image) return
      this.cropper = new Cropper(this.$refs.image, {
        viewMode: 1, dragMode: 'move', autoCropArea: 1, responsive: true, restore: false,
        guides: true, center: true, background: false, movable: true, rotatable: true,
        scalable: true, zoomable: true, zoomOnTouch: true, zoomOnWheel: true, wheelZoomRatio: 0.08,
        cropBoxMovable: true, cropBoxResizable: true, toggleDragModeOnDblclick: false,
        preview: '.cropper-live-preview',
        ready: () => {
          const image = this.cropper.getImageData(); const canvas = this.cropper.getCanvasData()
          this.originalSize = { width: Math.round(image.naturalWidth), height: Math.round(image.naturalHeight) }
          this.initialZoom = image.naturalWidth > 0 ? canvas.width / image.naturalWidth : 1
          this.ready = true; this.updateCropSize(); this.pushHistory(); this.emitState()
        },
        crop: () => { this.updateCropSize(); this.snapshotSoon() }
      })
    },
    destroyCropper() { if (this.cropper) this.cropper.destroy(); this.cropper = null; if (this.sourceUrl) URL.revokeObjectURL(this.sourceUrl); this.sourceUrl = '' },
    resetState() {
      this.ready = false; this.edited = false; this.initialZoom = 1; this.zoomPercent = 100
      this.scaleX = 1; this.scaleY = 1; this.aspectRatioKey = 'free'; this.aspectLocked = false
      this.customOutputSize = false; this.outputWidth = 1; this.outputHeight = 1; this.outputFormat = 'original'; this.quality = 92
      this.originalSize = { width: 0, height: 0 }; this.cropSize = { width: 1, height: 1 }; this.history = []; this.historyIndex = -1; this.emitState()
    },
    resetEditor() { if (!this.cropper) return; this.cropper.reset(); this.cropper.setAspectRatio(NaN); this.resetStateAfterReady(); this.pushHistory() },
    resetStateAfterReady() {
      this.ready = true; this.edited = false; this.zoomPercent = 100; this.scaleX = 1; this.scaleY = 1
      this.aspectRatioKey = 'free'; this.aspectLocked = false; this.customOutputSize = false; this.outputFormat = 'original'; this.quality = 92
      this.updateCropSize(); this.emitState()
    },
    rotate(degrees) { if (this.cropper) { this.cropper.rotate(degrees); this.markEdited(); this.snapshotSoon() } },
    flipHorizontal() { if (this.cropper) { this.scaleX *= -1; this.cropper.scaleX(this.scaleX); this.markEdited(); this.snapshotSoon() } },
    flipVertical() { if (this.cropper) { this.scaleY *= -1; this.cropper.scaleY(this.scaleY); this.markEdited(); this.snapshotSoon() } },
    changeZoom(value) { if (this.cropper && this.ready) { this.cropper.zoomTo(this.initialZoom * Number(value || 100) / 100); this.markEdited(); this.snapshotSoon() } },
    changeAspectRatio(key) {
      if (!this.cropper) return
      const ratios = { '1:1': 1, '4:3': 4 / 3, '3:2': 3 / 2, '16:9': 16 / 9, '9:16': 9 / 16 }
      this.aspectLocked = key !== 'free'; this.customOutputSize = false; this.cropper.setAspectRatio(ratios[key] || NaN); this.markEdited(); this.snapshotSoon()
    },
    toggleAspectLock() {
      this.aspectLocked = !this.aspectLocked
      if (this.aspectLocked && this.cropper) this.cropper.setAspectRatio(this.outputWidth / this.outputHeight)
      else if (this.cropper) { this.aspectRatioKey = 'free'; this.cropper.setAspectRatio(NaN) }
      this.markEdited()
    },
    changeOutputSize(changed) {
      let width = Math.max(1, Math.min(8192, Number(this.outputWidth || 1)))
      let height = Math.max(1, Math.min(8192, Number(this.outputHeight || 1)))
      const ratio = this.cropSize.width / this.cropSize.height
      if (this.aspectLocked && ratio > 0) {
        if (changed === 'width') height = Math.round(width / ratio)
        else width = Math.round(height * ratio)
      }
      if (width * height > 40000000) { this.$message.warning('输出图片不能超过 4000 万像素'); return this.updateCropSize() }
      this.outputWidth = width; this.outputHeight = height; this.customOutputSize = true
      if (this.cropper) this.cropper.setAspectRatio(width / height)
      this.markEdited(); this.snapshotSoon()
    },
    updateCropSize() {
      if (!this.cropper) return
      const data = this.cropper.getData(true)
      this.cropSize = { width: Math.max(1, Math.round(data.width || 1)), height: Math.max(1, Math.round(data.height || 1)) }
      if (!this.customOutputSize) { this.outputWidth = this.cropSize.width; this.outputHeight = this.cropSize.height }
    },
    snapshotSoon() { clearTimeout(this.historyTimer); this.historyTimer = setTimeout(this.pushHistory, 180) },
    pushHistory() {
      if (!this.cropper || !this.ready || this.restoringHistory) return
      const state = { data: this.cropper.getData(), canvas: this.cropper.getCanvasData(), zoom: this.zoomPercent, scaleX: this.scaleX, scaleY: this.scaleY }
      this.history = this.history.slice(0, this.historyIndex + 1); this.history.push(state)
      if (this.history.length > 30) this.history.shift(); this.historyIndex = this.history.length - 1
    },
    restoreHistory(index) {
      const state = this.history[index]; if (!state || !this.cropper) return
      this.restoringHistory = true; this.cropper.setCanvasData(state.canvas); this.cropper.setData(state.data)
      this.zoomPercent = state.zoom; this.scaleX = state.scaleX; this.scaleY = state.scaleY; this.historyIndex = index
      this.$nextTick(() => { this.restoringHistory = false; this.updateCropSize(); this.markEdited() })
    },
    undo() { if (this.historyIndex > 0) this.restoreHistory(this.historyIndex - 1) },
    redo() { if (this.historyIndex < this.history.length - 1) this.restoreHistory(this.historyIndex + 1) },
    markEdited() { if (this.ready) { this.edited = true; this.emitState() } },
    emitState() { this.$emit('state-change', { ready: this.ready, edited: this.edited }) },
    async exportFile() {
      if (!this.ready || !this.cropper) throw new Error('图片编辑器尚未准备完成')
      const canvas = this.cropper.getCroppedCanvas({
        width: this.outputWidth, height: this.outputHeight, maxWidth: 8192, maxHeight: 8192,
        fillColor: this.outputMime === 'image/jpeg' ? '#fff' : 'transparent', imageSmoothingEnabled: true, imageSmoothingQuality: 'high'
      })
      if (!canvas) throw new Error('无法生成编辑后的图片')
      const mime = this.outputMime
      const blob = await new Promise(resolve => canvas.toBlob(resolve, mime, this.quality / 100))
      if (!blob) throw new Error('浏览器无法导出所选格式')
      return { file: new File([blob], this.outputFileName(mime), { type: mime, lastModified: Date.now() }), width: canvas.width, height: canvas.height, edited: true }
    },
    outputFileName(mime) { return (String(this.file.name || 'image').replace(/\.[^.]+$/, '') || 'image') + '.' + (MIME_EXTENSIONS[mime] || 'png') },
    formatBytes(bytes) { const value = Number(bytes || 0); return value < 1024 ? value + ' B' : value < 1048576 ? (value / 1024).toFixed(1) + ' KB' : (value / 1048576).toFixed(2) + ' MB' }
  }
}
</script>

<style lang="scss" scoped>
.image-editor { display: grid; overflow: hidden; align-items: start; border: 1px solid #dfe5ed; border-radius: 12px; background: #fff; grid-template-columns: minmax(0, 1fr) 286px; }
.editor-main { min-width: 0; padding: 14px; background: #f5f7fa; }
.editor-heading { display: flex; margin-bottom: 10px; align-items: center; justify-content: space-between; strong, span { display: block; } strong { color: #253044; font-size: 14px; } span { margin-top: 3px; color: #8a94a3; font-size: 11px; } }
.cropper-stage { overflow: hidden; height: clamp(320px, 43vh, 440px); border-radius: 8px; background: #202631; img { display: block; max-width: 100%; } }
.compact-toolbar { display: flex; min-height: 42px; margin-top: 10px; align-items: center; justify-content: space-between; gap: 14px; }
.compact-toolbar .el-button { padding: 9px 11px; }
.vertical-flip-button ::v-deep i { transform: rotate(90deg); }
.zoom-control { display: grid; min-width: 280px; flex: 1; align-items: center; color: #7d8796; grid-template-columns: auto minmax(100px, 1fr) auto 82px auto; gap: 7px; }
.zoom-control ::v-deep .el-input-number { width: 82px; }
.editor-panel { padding: 14px; border-left: 1px solid #e6eaf0; }
.panel-section + .panel-section { margin-top: 13px; padding-top: 13px; border-top: 1px solid #edf0f4; }
.section-title { display: flex; margin-bottom: 9px; justify-content: space-between; color: #253044; font-size: 13px; font-weight: 600; span { color: #477eb8; font-size: 11px; font-weight: 400; } }
.preview-shell { display: flex; overflow: hidden; height: 108px; align-items: center; justify-content: center; border: 1px solid #e1e6ed; border-radius: 7px; background: #f5f7fa; }
.cropper-live-preview { overflow: hidden; width: 100%; height: 100%; }
.ratio-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.ratio-grid ::v-deep .el-radio-button__inner { width: 100%; padding: 7px 5px; border: 1px solid #dcdfe6; border-radius: 5px; box-shadow: none; }
.dimension-row { display: grid; align-items: center; grid-template-columns: 1fr 34px 1fr; gap: 6px; }
.dimension-row ::v-deep .el-input-number { width: 100%; }
.ratio-lock { width: 34px; padding: 9px 0; }
.dimension-labels { display: flex; margin-top: 4px; justify-content: space-between; color: #a0a7b2; font-size: 10px; }
.setting-warning { margin-top: 7px; color: #e6a23c; font-size: 11px; }
.output-section .el-select { width: 100%; }
.quality-heading { display: flex; margin-top: 10px; justify-content: space-between; color: #606b7b; font-size: 11px; span { color: #477eb8; } }
.original-meta { display: flex; margin-top: 13px; padding: 9px; align-items: center; border-radius: 7px; background: #f6f8fb; color: #7c8796; i { margin-right: 8px; font-size: 19px; } strong, span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; } strong { max-width: 220px; color: #4d5969; font-size: 11px; } span { margin-top: 3px; font-size: 10px; } }
@media (max-width: 900px) { .image-editor { grid-template-columns: 1fr; } .editor-panel { border-top: 1px solid #e6eaf0; border-left: 0; } .compact-toolbar { align-items: stretch; flex-direction: column; } }
</style>
